// useHeartbeat.js
// Sends a heartbeat every ~25s while the tab is visible, pausing when backgrounded.
// Mount this once, high up - e.g. inside App.js's Layout - and pass in whether the
// client is currently logged in. Re-evaluating this on every render (rather than
// checking login status once inside the effect) is what lets the heartbeat actually
// start right after login, since Layout itself never unmounts/remounts across routes.

import { useEffect, useRef } from "react";
import { clientApi } from "./clientApi";

const HEARTBEAT_INTERVAL_MS = 25000;

export default function useHeartbeat(enabled) {
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!enabled) return;

    function sendHeartbeat() {
      // Only beat while the tab is actually visible - avoids inflating time-spent
      // numbers for tabs left open in the background.
      if (document.visibilityState === "visible") {
        clientApi.heartbeat().catch(() => {
          // Silently ignore - a missed heartbeat just means the session might get
          // marked stale a bit early server-side, not worth surfacing to the user.
        });
      }
    }

    // Fire one immediately on mount, then on the interval.
    sendHeartbeat();
    intervalRef.current = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS);

    function handleVisibilityChange() {
      if (document.visibilityState === "visible") {
        sendHeartbeat(); // catch up immediately when the tab regains focus
      }
    }
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearInterval(intervalRef.current);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [enabled]);
}